// components/scheduler/ReminderConfigForm.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Modal, Form, Input, Select, InputNumber, Switch, TimePicker, Divider, Alert, Button } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { Languages, NotificationPlatforms, QueryServiceInfo } from '@/shared/api/model';
import QueryServiceSelector from './QueryServiceSelector';
import DynamicFilterForm from './DynamicFilterForm';
import dayjs from 'dayjs';
import { executeQuery } from "@/shared/api/client/notification-process-service-controller";
import {useTemplateOptions} from "@/modules/customer-notification/hooks/useTemplateOptions";

const { Option } = Select;
const { TextArea } = Input;

interface ReminderConfigFormProps {
    visible: boolean;
    onCancel: () => void;
    formData: any;
    isEditMode: boolean;
    isLoading: boolean;
    updateFormField: (field: string, value: any) => void;
    saveConfig: () => Promise<boolean>;
    queryServices: QueryServiceInfo[];
    isLoadingServices: boolean;
}

const ReminderConfigForm: React.FC<ReminderConfigFormProps> = ({
                                                                   visible,
                                                                   onCancel,
                                                                   formData,
                                                                   isEditMode,
                                                                   isLoading,
                                                                   updateFormField,
                                                                   saveConfig,
                                                                   queryServices,
                                                                   isLoadingServices
                                                               }) => {
    const [form] = Form.useForm();
    const { dark } = useTheme();
    const [selectedService, setSelectedService] = useState<string | null>(formData.queryServiceName || null);
    const [timeValue, setTimeValue] = useState<dayjs.Dayjs | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    // Use a ref to store the actual time values independent of React's state management
    const timeRef = useRef({ hour: 8, minute: 0 });

    // Get template options based on selected platform
    const { templateOptions, isLoading: isLoadingTemplates } = useTemplateOptions(
        formData.platform as NotificationPlatforms
    );

    // Set initial time value when form opens
    useEffect(() => {
        if (visible) {
            // Create time from hours and minutes directly from formData
            const hour = formData.reminderHour !== undefined ? formData.reminderHour : 8;
            const minute = formData.reminderMinute !== undefined ? formData.reminderMinute : 0;

            console.log(`Initial formData hours/minutes: ${hour}:${minute}`);

            // Store in our ref for safekeeping
            timeRef.current = { hour, minute };

            // Create the time object
            const initialTime = dayjs().hour(hour).minute(minute);
            console.log(`Setting initial time to ${hour}:${minute}`);
            setTimeValue(initialTime);

            // Set form values
            form.setFieldsValue({
                ...formData,
                reminderTime: initialTime,
                filters: formData.queryFilters || {}
            });

            // Set selected service
            setSelectedService(formData.queryServiceName || null);
        }
    }, [visible]);

    // Reset form when dialog closes
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // Time change handler
    const handleTimeChange = (value: dayjs.Dayjs | null) => {
        if (!value) return;

        // Get values in 24-hour format
        const hour = value.hour();
        const minute = value.minute();

        console.log(`Time changed: ${hour}:${minute}`);

        // Update local state
        setTimeValue(value);

        // Store in our ref - this won't be affected by React re-renders
        timeRef.current = { hour, minute };
    };

    // Handle time OK button
    const handleTimeOk = (value: dayjs.Dayjs | null) => {
        if (!value) return;

        const hour = value.hour();
        const minute = value.minute();

        console.log(`Time OK button: ${hour}:${minute}`);

        // Update local state
        setTimeValue(value);

        // Update our ref
        timeRef.current = { hour, minute };

        // Try to update the parent state, but don't rely on it
        updateFormField('reminderHour', hour);
        updateFormField('reminderMinute', minute);
    };

    // Handle service change
    const handleServiceChange = (serviceName: string) => {
        setSelectedService(serviceName);
        updateFormField('queryServiceName', serviceName);
        updateFormField('queryFilters', {});
        form.setFieldsValue({ filters: {} });
    };

    // Handle filter changes
    const handleFiltersChange = (filters: Record<string, any>) => {
        updateFormField('queryFilters', filters);
    };

    // Field change handler
    const handleFieldChange = (fieldName: string, value: any) => {
        updateFormField(fieldName, value);

        // Special handling for platform changes
        if (fieldName === 'platform') {
            // Reset template selection when platform changes
            updateFormField('templateName', '');
        }
    };

    // Form submission handler - create a clean copy of data to send
    const handleSubmit = async () => {
        try {
            // Validate all fields
            await form.validateFields();

            // Get current form values
            const values = form.getFieldsValue();

            // Create a fresh copy of the data to submit
            const dataToSubmit = {
                ...formData,
                ...values,
                // Most importantly: use our ref for the time values
                reminderHour: timeRef.current.hour,
                reminderMinute: timeRef.current.minute,
                // Ensure filters are included
                queryFilters: values.filters || formData.queryFilters || {}
            };

            console.log("FINAL DATA FOR SUBMISSION:", {
                hour: timeRef.current.hour,
                minute: timeRef.current.minute,
                dataToSubmit: dataToSubmit
            });

            // DIRECT SUBMISSION BYPASSING REACT STATE
            // This is a hack, but it ensures the correct values are used
            const success = await saveCustomConfig(dataToSubmit);
            if (success) {
                handleCancel();
            }

        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    // Direct submission helper
    const saveCustomConfig = async (configData: any) => {
        try {
            // Override the normal save process with our clean data
            console.log("USING DIRECT SUBMISSION WITH DATA:", configData);

            // We have to update parent formData temporarily to make the API call work
            Object.keys(configData).forEach(key => {
                updateFormField(key, configData[key]);
            });

            // Small delay to let updates settle
            await new Promise(resolve => setTimeout(resolve, 50));

            // Call the normal save function which will use the API
            return await saveConfig();
        } catch (error) {
            console.error("Error in direct submission:", error);
            return false;
        }
    };

    // Handle preview button click
    const handlePreview = async () => {
        try {
            // Get current form values
            const values = await form.validateFields(['queryServiceName', 'filters']);

            if (!values.queryServiceName) {
                return;
            }

            setIsPreviewLoading(true);

            // Execute query to get count
            const response = await executeQuery(values.queryServiceName, values.filters || {});

            // Show preview modal with count
            Modal.info({
                title: 'Notification Preview',
                content: (
                    <div>
                        <p>This notification would be sent to approximately {response} recipients.</p>
                        <p>
                            {response === 0 ?
                                'No recipients match the current filter criteria.' :
                                'You can adjust the filters to target specific recipients.'
                            }
                        </p>
                    </div>
                ),
                okText: 'Close'
            });
        } catch (error) {
            console.error('Error previewing notifications', error);
        } finally {
            setIsPreviewLoading(false);
        }
    };

    return (
        <Modal
            title={isEditMode ? 'Edit Reminder Configuration' : 'Create Reminder Configuration'}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={isLoading}
            width={800}
            className={dark ? 'dark' : ''}
        >
            <Form
                form={form}
                layout="vertical"
                className="mt-4"
                initialValues={{
                    ...formData,
                    reminderTime: timeValue,
                    filters: formData.queryFilters || {}
                }}
            >
                <Form.Item
                    name="name"
                    label="Configuration Name"
                    rules={[
                        { required: true, message: 'Please enter configuration name' },
                        { max: 50, message: 'Name cannot be longer than 50 characters' }
                    ]}
                >
                    <Input
                        placeholder="e.g., Loan Payment 3-Day Reminder"
                        disabled={isEditMode}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Description"
                >
                    <TextArea
                        rows={2}
                        placeholder="Optional description of this reminder configuration"
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                    />
                </Form.Item>

                <Divider>Notification Type</Divider>

                <QueryServiceSelector
                    services={queryServices}
                    isLoading={isLoadingServices}
                    value={selectedService || undefined}
                    onChange={handleServiceChange}
                />

                {selectedService && (
                    <DynamicFilterForm
                        serviceName={selectedService}
                        value={formData.queryFilters || {}}
                        onChange={handleFiltersChange}
                    />
                )}

                {selectedService && (
                    <div className="mt-2 mb-4">
                        <Button
                            onClick={handlePreview}
                            disabled={isLoading}
                            loading={isPreviewLoading}
                        >
                            Preview Recipients
                        </Button>
                    </div>
                )}

                <Divider>Template Configuration</Divider>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                        name="platform"
                        label="Platform"
                        rules={[{ required: true, message: 'Please select platform' }]}
                    >
                        <Select
                            placeholder="Select platform"
                            disabled={isEditMode}
                            onChange={(value) => handleFieldChange('platform', value)}
                        >
                            <Option value={NotificationPlatforms.SMS}>SMS</Option>
                            <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
                            <Option value={NotificationPlatforms.EMAIL}>Email</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="language"
                        label="Language"
                        rules={[{ required: true, message: 'Please select language' }]}
                    >
                        <Select
                            placeholder="Select language"
                            onChange={(value) => handleFieldChange('language', value)}
                        >
                            <Option value={Languages.EN}>English</Option>
                            <Option value={Languages.SW}>Swahili</Option>
                        </Select>
                    </Form.Item>
                </div>

                <Form.Item
                    name="templateName"
                    label="Message Template"
                    rules={[{ required: true, message: 'Please select a template' }]}
                    help={!formData.platform ? "Select a platform first to see available templates" : ""}
                >
                    <Select
                        placeholder={formData.platform ? "Select template" : "Select platform first"}
                        loading={isLoadingTemplates}
                        disabled={!formData.platform}
                        onChange={(value) => handleFieldChange('templateName', value)}
                    >
                        {templateOptions.map(option => (
                            <Option key={option.name} value={option.name}>
                                {option.displayName}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Divider>Schedule Configuration</Divider>

                <Alert
                    message="Reminder Schedule"
                    description="Configure when reminders should be sent."
                    type="info"
                    showIcon
                    className="mb-4"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Form.Item
                        name="reminderTime"
                        label="Time of Day (24-hour format)"
                        tooltip="The time of day when reminders will be sent"
                        rules={[{ required: true, message: 'Please select time of day' }]}
                    >
                        <TimePicker
                            format="HH:mm"
                            className="w-full"
                            value={timeValue}
                            onChange={handleTimeChange}
                            onOk={handleTimeOk}
                            allowClear={false}
                            inputReadOnly={true}
                        />
                    </Form.Item>

                    <Form.Item
                        name="retryCount"
                        label="Retry Count"
                        tooltip="Number of retry attempts if the first attempt fails"
                    >
                        <InputNumber
                            min={0}
                            max={5}
                            className="w-full"
                            onChange={(value) => handleFieldChange('retryCount', value)}
                        />
                    </Form.Item>

                    <Form.Item
                        name="enabled"
                        label="Enable"
                        valuePropName="checked"
                        tooltip="Toggle to enable or disable this reminder configuration"
                    >
                        <Switch
                            onChange={(checked) => handleFieldChange('enabled', checked)}
                        />
                    </Form.Item>
                </div>

                {/* Hidden debug field to help diagnose issues */}
                <div style={{ display: 'none' }}>
                    Current time values: {timeRef.current.hour}:{timeRef.current.minute}
                </div>
            </Form>
        </Modal>
    );
};

export default ReminderConfigForm;